// NoCatalogue order flow — cart-based product request with shipping address checkout
// Mirrors the catalog product selection flow's UI patterns (header, cart sheet, footer)

import { TRPCClientError } from "@trpc/client";
import { resolveCountryName, resolveRegionName } from "@/lib/data/country-data";
import { PHONE_REGEX } from "@/lib/validations";
import { useAutoSelectPrimaryAddress } from "@/lib/hooks/use-auto-select-primary-address";
import {
  isAddressComplete,
  normalizeCountryCode,
  type MemberAddress,
} from "@/lib/queries/member-data";
import {
  useSubmitNoCatalogueOrderMutation,
  type NoCatalogueCartItem as NoCatalogueCartItemType,
} from "@/lib/queries/products";
import {
  Button,
  Card,
  CardContent,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
  toast,
} from "@repo/ui";
import DOMPurify from "dompurify";
import {
  AlertCircle,
  CheckCircle2,
  ClipboardList,
  Package,
  ShoppingCart,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUpdateProfileAddress } from "@/lib/queries/profile";
import { AddressSelector, type ProfileAddress } from "./address-selector";
import {
  ContactInfoSection,
  isContactInfoComplete,
  type ContactInfo,
} from "./contact-info-section";
import {
  ProductRequestForm,
  type NoCatalogueConfig,
} from "./product-request-form";
import { ProductSelectionHeader } from "./product-selection-header";
import { ShippingAddressStep } from "./shipping-address-step";

interface MemberField {
  alias: string;
  value: unknown;
}

interface MemberInfo {
  id: number;
  name?: string | null;
  email?: string | null;
}

interface OrderRequest {
  id: string;
  orderRequestStatus?: string | null;
  member?: { id: number; name?: string; email?: string } | null;
}

interface MemberData {
  member: MemberInfo | null;
  fields: MemberField[];
}

interface NoCatalogueOrderFlowProps {
  noCatalogueConfig: NoCatalogueConfig;
  instructionText?: string | null;
  interactionId: string;
  organizationId: string;
  orderKey: string;
  orderRequest: OrderRequest;
  memberData: MemberData | undefined;
  quantityMax?: number | null;
  profileAddresses?: ProfileAddress[];
  /** Verified email from the creator's profile (used when a profile address is selected). */
  profileEmail?: string | null;
  /** Profile ID for saving phone back to profile address after order submission. */
  profileId?: string;
}

export function NoCatalogueOrderFlow({
  noCatalogueConfig,
  instructionText,
  interactionId,
  organizationId,
  orderKey,
  orderRequest,
  memberData,
  quantityMax,
  profileAddresses,
  profileEmail,
  profileId,
}: NoCatalogueOrderFlowProps) {
  const cartStorageKey = `nocatalogue-cart-${organizationId}-${interactionId}`;

  const [cart, setCart] = useState<NoCatalogueCartItemType[]>(() => {
    try {
      const stored = sessionStorage.getItem(cartStorageKey);
      return stored ? (JSON.parse(stored) as NoCatalogueCartItemType[]) : [];
    } catch {
      return [];
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [editedAddress, setEditedAddress] = useState<MemberAddress | null>(
    null,
  );
  const [contactInfoMode, setContactInfoMode] = useState<"same" | "different">(
    "same",
  );
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [submitErrors, setSubmitErrors] = useState<
    Partial<Record<"address" | "contact" | "cart" | "submit", string>>
  >({});

  const rawInstructionHtml =
    instructionText ?? noCatalogueConfig.additionalDetails ?? "";

  const safeHtml = useMemo(
    () => DOMPurify.sanitize(rawInstructionHtml),
    [rawInstructionHtml],
  );

  const submitMutation = useSubmitNoCatalogueOrderMutation();

  // Existing persistence logic
  const prevCartRef = useRef(cart);
  useEffect(() => {
    if (prevCartRef.current !== cart) {
      prevCartRef.current = cart;
      try {
        if (cart.length > 0) {
          sessionStorage.setItem(cartStorageKey, JSON.stringify(cart));
        } else {
          sessionStorage.removeItem(cartStorageKey);
        }
      } catch {
        // sessionStorage quota exceeded or unavailable
      }
    }
  }, [cart, cartStorageKey]);

  // Build initial address from member data fields
  const currentMember = memberData?.member ?? null;
  const fieldValue = (alias: string) =>
    String(
      memberData?.fields.find((f: MemberField) => f.alias === alias)?.value ??
        "",
    );
  const initialAddress: MemberAddress | null = useMemo(
    () =>
      currentMember
        ? {
            firstName:
              fieldValue("Address First Name") ||
              currentMember.name?.split(" ")[0] ||
              "",
            lastName:
              fieldValue("Address Last Name") ||
              currentMember.name?.split(" ").slice(1).join(" ") ||
              "",
            address1: fieldValue("Address1"),
            address2: fieldValue("Address2"),
            city: fieldValue("City"),
            country: fieldValue("Country"),
            province: fieldValue("State"),
            zip: fieldValue("PostalCode"),
            phone: fieldValue("Phone"),
          }
        : null,
    // Referential stability needed for clear-errors useEffect
    [currentMember, memberData?.fields],
  );
  const memberAddress = editedAddress ?? initialAddress;
  const memberEmail = currentMember?.email ?? null;
  const addressReady = memberAddress ? isAddressComplete(memberAddress) : false;

  // Auto-select primary profile address when no existing address is set
  useAutoSelectPrimaryAddress(
    profileAddresses,
    editedAddress,
    initialAddress,
    setEditedAddress,
  );

  // Use profile email when the selected address matches a profile address (EPD-5898)
  const isProfileAddress = profileAddresses?.some((addr) => {
    const addrKey = [
      addr.firstName || "",
      addr.lastName || "",
      addr.line1 || "",
      addr.line2 || "",
      addr.city || "",
      addr.state || "",
      addr.zip || "",
      normalizeCountryCode(addr.country || ""),
    ]
      .join("|")
      .toLowerCase();
    const currentKey = [
      memberAddress?.firstName || "",
      memberAddress?.lastName || "",
      memberAddress?.address1 || "",
      memberAddress?.address2 || "",
      memberAddress?.city || "",
      memberAddress?.province || "",
      memberAddress?.zip || "",
      normalizeCountryCode(memberAddress?.country || ""),
    ]
      .join("|")
      .toLowerCase();
    return addrKey === currentKey;
  });
  const effectiveEmail =
    isProfileAddress && profileEmail ? profileEmail : memberEmail || "";

  // Contact info completeness
  const contactInfoComplete = isContactInfoComplete(
    memberAddress,
    effectiveEmail || null,
  );

  const currentAddressKey = [
    memberAddress?.firstName || "",
    memberAddress?.lastName || "",
    memberAddress?.address1 || "",
    memberAddress?.address2 || "",
    memberAddress?.city || "",
    memberAddress?.province || "",
    memberAddress?.zip || "",
    normalizeCountryCode(memberAddress?.country || ""),
  ]
    .join("|")
    .toLowerCase();

  const selectedProfileAddressId =
    profileAddresses?.find((addr) => {
      const addrKey = [
        addr.firstName || "",
        addr.lastName || "",
        addr.line1 || "",
        addr.line2 || "",
        addr.city || "",
        addr.state || "",
        addr.zip || "",
        normalizeCountryCode(addr.country || ""),
      ]
        .join("|")
        .toLowerCase();
      return addrKey === currentAddressKey;
    })?.id ?? null;

  // Mutation for saving phone back to profile address
  const updateProfileAddressMutation = useUpdateProfileAddress(organizationId);

  // Cart calculations
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const exceedsMax = quantityMax != null ? cartItemCount > quantityMax : false;

  // Contact validation (phone format must pass PHONE_REGEX in both modes)
  const isContactInfoValid =
    contactInfoMode === "same"
      ? !!effectiveEmail &&
        !!memberAddress?.phone &&
        PHONE_REGEX.test(memberAddress.phone)
      : !!(
          contactInfo.firstName &&
          contactInfo.lastName &&
          contactInfo.email &&
          contactInfo.phone &&
          PHONE_REGEX.test(contactInfo.phone)
        );

  const isCartValid =
    cart.length > 0 && !exceedsMax && addressReady && isContactInfoValid;

  // Clear submit errors when form state changes
  useEffect(() => {
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
    setSubmitErrors((prev) => (Object.keys(prev).length > 0 ? {} : prev));
  }, [memberAddress, contactInfo, contactInfoMode, effectiveEmail, cart]);

  // Build a selection rule object for ConditionsDialog (matching catalog flow's shape)
  const selectionRule = quantityMax
    ? {
        label: "Order Conditions",
        description: null as string | null,
        maxQuantity: quantityMax,
        minQuantity: null as number | null,
        maxPrice: null as number | null,
      }
    : null;

  // Already submitted?
  const isAlreadySubmitted =
    orderRequest.orderRequestStatus === "PENDING" ||
    orderRequest.orderRequestStatus === "ACCEPTED";

  // Handlers
  const handleAddToCart = useCallback((formData: Record<string, string>) => {
    const newItem: NoCatalogueCartItemType = {
      id: crypto.randomUUID(),
      quantity: parseInt(formData.quantity, 10) || 1,
      title:
        formData.productName || formData.product_name || "Untitled Product",
      selections: formData.productPreferences || formData.preferences || "",
      url: formData.productURL || formData.product_url || undefined,
      sku: formData.productSKU || formData.product_sku || undefined,
      formData,
    };
    setCart((prev: NoCatalogueCartItemType[]) => [...prev, newItem]);
  }, []);

  const handleRemoveItem = useCallback((id: string) => {
    setCart((prev: NoCatalogueCartItemType[]) =>
      prev.filter((item) => item.id !== id),
    );
  }, []);

  const handleAddressChange = useCallback(
    (address: MemberAddress) => {
      setEditedAddress(address);
      // Persist edits back to the profile address if one is currently selected.
      // Profile settings stores full names (e.g. "United States") while the
      // shipping form works with ISO codes (e.g. "US"), so resolve back to
      // full names before saving.
      if (selectedProfileAddressId && profileId) {
        const countryName =
          resolveCountryName(address.country) ?? address.country;
        const stateName = address.province
          ? (resolveRegionName(countryName, address.province) ??
            address.province)
          : undefined;
        updateProfileAddressMutation.mutate(
          {
            profileId,
            addressId: selectedProfileAddressId,
            address: {
              firstName: address.firstName || undefined,
              lastName: address.lastName || undefined,
              phone: address.phone || undefined,
              line1: address.address1 || undefined,
              line2: address.address2 || undefined,
              city: address.city || undefined,
              state: stateName,
              zip: address.zip || undefined,
              country: countryName || undefined,
            },
          },
          {
            onError: () => {
              setSubmitErrors((prev) => ({
                ...prev,
                address: "Failed to save address. Please try again.",
              }));
            },
          },
        );
      }
    },
    [selectedProfileAddressId, profileId, updateProfileAddressMutation],
  );

  const handleSubmitOrder = useCallback(async () => {
    const errors: Partial<Record<"address" | "contact" | "cart", string>> = {};

    if (cart.length === 0) {
      errors.cart = "Please add at least one product to your order";
    } else if (quantityMax != null && cartItemCount > quantityMax) {
      errors.cart = `Maximum ${quantityMax} ${quantityMax === 1 ? "product" : "products"} allowed. You have ${cartItemCount}.`;
    }

    if (!memberAddress || !isAddressComplete(memberAddress)) {
      errors.address = "Please enter and confirm your shipping address";
    }

    const memberId = orderRequest.member?.id;
    if (typeof memberId !== "number" || memberId <= 0) {
      errors.address =
        errors.address || "Invalid member ID. Please contact support.";
    }

    // Validate email exists for the chosen contact info mode
    const resolvedEmail =
      contactInfoMode === "same" ? effectiveEmail : contactInfo.email;
    if (!resolvedEmail) {
      errors.contact = "Please provide an email address for this order";
    }

    // Validate contact info fields when using different contact details
    if (contactInfoMode === "different") {
      if (!contactInfo.firstName || !contactInfo.lastName) {
        errors.contact =
          errors.contact ||
          "Please provide your first and last name in contact info";
      }
    }

    // Resolve and validate phone from the active contact source
    const rawPhone =
      contactInfoMode === "same" ? memberAddress?.phone : contactInfo.phone;
    const validatedPhone =
      rawPhone && PHONE_REGEX.test(rawPhone) ? rawPhone : undefined;

    if (!validatedPhone) {
      errors.contact = errors.contact || "Please provide a valid phone number";
    }

    if (Object.keys(errors).length > 0) {
      setSubmitErrors(errors);
      return;
    }
    setSubmitErrors({});

    // Type narrowing: all validation passed — these are guaranteed non-null
    if (!memberAddress || typeof memberId !== "number" || !validatedPhone) {
      return;
    }

    const finalMember =
      contactInfoMode === "same"
        ? {
            id: memberId,
            name: `${memberAddress.firstName} ${memberAddress.lastName}`,
            email: effectiveEmail,
          }
        : {
            id: memberId,
            name: `${contactInfo.firstName} ${contactInfo.lastName}`,
            email: contactInfo.email,
          };

    const finalShippingAddress =
      contactInfoMode === "same"
        ? {
            first_name: memberAddress.firstName,
            last_name: memberAddress.lastName,
            email: effectiveEmail,
            phone: validatedPhone,
            address1: memberAddress.address1,
            address2: memberAddress.address2 || undefined,
            city: memberAddress.city,
            province: memberAddress.province || "",
            country: memberAddress.country,
            zip: memberAddress.zip,
          }
        : {
            first_name: contactInfo.firstName,
            last_name: contactInfo.lastName,
            email: contactInfo.email,
            phone: validatedPhone,
            address1: memberAddress.address1,
            address2: memberAddress.address2 || undefined,
            city: memberAddress.city,
            province: memberAddress.province || "",
            country: memberAddress.country,
            zip: memberAddress.zip,
          };

    try {
      await submitMutation.mutateAsync({
        organizationId,
        orderKey,
        orderRequestId: orderRequest.id,
        profileId,
        member: finalMember,
        noCatalogueLineItems: cart.map((item) => item.formData),
        shippingAddress: finalShippingAddress,
      });

      // Save phone back to profile address if user provided one in "different" mode
      if (
        contactInfoMode === "different" &&
        contactInfo.phone &&
        selectedProfileAddressId &&
        profileId
      ) {
        updateProfileAddressMutation.mutate({
          profileId,
          addressId: selectedProfileAddressId,
          address: { phone: contactInfo.phone },
        });
      }

      toast.success("Order submitted successfully!");
      setCart([]);
      setIsCartOpen(false);
      try {
        sessionStorage.removeItem(cartStorageKey);
      } catch {
        // ignore
      }
    } catch (error) {
      if (error instanceof TRPCClientError && error.data?.httpStatus === 400) {
        // BAD_REQUEST: user-facing validation error (e.g. out of stock, inventory limit)
        console.warn("Order submission validation error:", error.message);
        setSubmitErrors({
          submit: error.message || "Failed to submit order. Please try again.",
        });
      } else {
        console.error("Order submission failed:", error);
        setSubmitErrors({
          submit: "Failed to submit order. Please try again.",
        });
      }
    }
  }, [
    memberAddress,
    effectiveEmail,
    organizationId,
    orderKey,
    orderRequest,
    cart,
    cartItemCount,
    quantityMax,
    submitMutation,
    cartStorageKey,
    contactInfoMode,
    contactInfo,
    selectedProfileAddressId,
    profileId,
    updateProfileAddressMutation,
  ]);

  if (isAlreadySubmitted) {
    return (
      <div className="px-4 sm:px-6 py-6">
        <Card className="mt-4">
          <CardContent className="py-8 text-center">
            <Package className="h-12 w-12 text-success-foreground mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Request Submitted</h2>
            <p className="text-muted-foreground">
              Your product request has been submitted and is being reviewed by
              the brand.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header — matches catalog flow layout */}
      <ProductSelectionHeader
        title="Product Request"
        description="Add products to your order and submit"
        effectiveSelectionRule={selectionRule}
        currencyCode="USD"
        profileAddresses={profileAddresses}
        // Pass a mocked cart for validation logic in conditions dialog
        cart={cart.map((c) => ({
          id: c.id,
          variantId: 0,
          variantTitle: c.title,
          price: "0",
          image: null,
          title: c.title,
          productTitle: c.title,
          productSlug: "",
          quantity: c.quantity,
        }))}
        // Custom Cart Sheet specific to NoCatalogue flow
        cartAction={
          <ResponsiveDialog open={isCartOpen} onOpenChange={setIsCartOpen}>
            <ResponsiveDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="relative w-full gap-1.5"
              >
                <ShoppingCart className="h-3.5 w-3.5" />
                Cart
                {cartItemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-teal-600 text-white text-xs font-medium rounded-full h-5 w-5 flex items-center justify-center">
                    {cartItemCount}
                  </span>
                )}
              </Button>
            </ResponsiveDialogTrigger>
            <ResponsiveDialogContent className="w-full sm:max-w-lg flex flex-col max-h-[90vh] overflow-hidden pb-0">
              <ResponsiveDialogHeader>
                <ResponsiveDialogTitle>Your Order</ResponsiveDialogTitle>
                <ResponsiveDialogDescription>
                  Review your selected products and proceed to checkout.
                </ResponsiveDialogDescription>
              </ResponsiveDialogHeader>

              {cart.length === 0 ? (
                <div className="flex-1 flex items-center justify-center min-h-[300px]">
                  <div className="text-center py-12">
                    <ShoppingCart className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      No products added yet
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto -mx-6 px-6 pb-6">
                    <div className="space-y-4 py-4">
                      {cart.map((item) => (
                        <div
                          key={item.id}
                          className="relative group bg-accent/30 rounded-lg p-3 border border-border flex gap-3"
                        >
                          <div className="h-16 w-16 bg-background rounded-md border border-border flex items-center justify-center shrink-0">
                            <Package className="h-8 w-8 text-muted-foreground/50" />
                          </div>
                          <div className="flex-1 min-w-0 pr-6">
                            <h4 className="font-medium truncate">
                              {item.title}
                            </h4>
                            <div className="text-sm text-muted-foreground mt-1">
                              Quantity: {item.quantity}
                            </div>
                            {item.sku && (
                              <div className="text-xs text-muted-foreground mt-0.5 font-mono">
                                SKU: {item.sku}
                              </div>
                            )}
                            {item.url && (
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline mt-1 inline-block truncate max-w-full"
                              >
                                {item.url}
                              </a>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemoveItem(item.id)}
                            className="text-muted-foreground hover:text-foreground p-1 -mr-2"
                            aria-label="Remove"
                          >
                            <X className="h-4 w-4" />
                            <span className="sr-only">Remove</span>
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="border-t border-border pt-4 mt-4 space-y-4 pb-4">
                      {/* Cart Summary with validation */}
                      <div className="space-y-4">
                        {/* Validation alerts */}
                        {exceedsMax && quantityMax && (
                          <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/30 dark:bg-destructive/20 dark:border-destructive rounded-lg">
                            <AlertCircle className="h-4 w-4 text-destructive-text mt-0.5 shrink-0" />
                            <p className="text-sm text-destructive-text">
                              Maximum {quantityMax}{" "}
                              {quantityMax === 1 ? "product" : "products"}{" "}
                              allowed. You have {cartItemCount}.
                            </p>
                          </div>
                        )}

                        {!exceedsMax && cart.length > 0 && quantityMax && (
                          <div className="flex items-start gap-2 p-3 bg-success/50 border border-success-foreground/20 rounded-lg">
                            <CheckCircle2 className="h-4 w-4 text-success-foreground mt-0.5 shrink-0" />
                            <p className="text-sm text-success-foreground">
                              Your selection meets all requirements.
                            </p>
                          </div>
                        )}

                        {/* Summary */}
                        <div className="bg-accent/50 rounded-lg p-4 space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Products
                            </span>
                            <span className="font-medium">{cart.length}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Total Items
                            </span>
                            <span className="font-medium">{cartItemCount}</span>
                          </div>
                          {quantityMax && (
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">
                                Maximum Item Limit
                              </span>
                              <span className="font-medium">{quantityMax}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Shipping Address */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="font-medium">Shipping Address</h3>
                          {!addressReady && (
                            <span className="text-xs font-medium bg-warning text-warning-foreground px-2 py-0.5 rounded-full">
                              Required
                            </span>
                          )}
                        </div>

                        {profileAddresses && profileAddresses.length > 0 && (
                          <div className="space-y-3">
                            <h4 className="font-medium text-sm">
                              Select Address
                            </h4>
                            <AddressSelector
                              profileAddresses={profileAddresses}
                              selectedAddressId={
                                profileAddresses.find(
                                  (addr) =>
                                    addr.firstName ===
                                      memberAddress?.firstName &&
                                    addr.lastName === memberAddress?.lastName &&
                                    addr.line1 === memberAddress?.address1 &&
                                    (addr.line2 || "") ===
                                      (memberAddress?.address2 || "") &&
                                    addr.city === memberAddress?.city &&
                                    addr.zip === memberAddress?.zip &&
                                    normalizeCountryCode(addr.country || "") ===
                                      normalizeCountryCode(
                                        memberAddress?.country || "",
                                      ),
                                )?.id ?? "new"
                              }
                              onAddressChange={handleAddressChange}
                            />
                          </div>
                        )}

                        <ShippingAddressStep
                          address={memberAddress}
                          onAddressChange={handleAddressChange}
                        />
                        {submitErrors.address && (
                          <p
                            role="alert"
                            className="text-sm text-destructive flex items-center gap-1.5"
                          >
                            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                            {submitErrors.address}
                          </p>
                        )}

                        {/* Contact Information */}
                        {addressReady && (
                          <ContactInfoSection
                            mode={contactInfoMode}
                            onModeChange={setContactInfoMode}
                            contactInfo={contactInfo}
                            onContactInfoChange={setContactInfo}
                            memberAddress={memberAddress}
                            memberEmail={effectiveEmail}
                            disableSameOption={!contactInfoComplete}
                          />
                        )}
                        {submitErrors.contact && (
                          <p
                            role="alert"
                            className="text-sm text-destructive flex items-center gap-1.5"
                          >
                            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                            {submitErrors.contact}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Submit Button - Sticky Footer */}
                  {cart.length > 0 && (
                    <ResponsiveDialogFooter className="border-t bg-background p-6 -mx-6 -mb-6 mt-auto">
                      <div className="w-full">
                        <Button
                          className="w-full"
                          onClick={handleSubmitOrder}
                          disabled={!isCartValid || submitMutation.isPending}
                        >
                          {submitMutation.isPending
                            ? "Submitting..."
                            : `Submit Order (${cartItemCount} ${
                                cartItemCount === 1 ? "item" : "items"
                              })`}
                        </Button>

                        {!addressReady && (
                          <p className="text-xs text-warning-foreground text-center mt-2">
                            Please confirm your shipping address before
                            submitting
                          </p>
                        )}
                        {addressReady &&
                          contactInfoMode === "same" &&
                          !effectiveEmail && (
                            <p className="text-xs text-warning-foreground text-center mt-2">
                              No email on file. Please select &quot;Update
                              contact info&quot; to provide one.
                            </p>
                          )}
                        {submitErrors.cart && (
                          <p
                            role="alert"
                            className="text-sm text-destructive flex items-center justify-center gap-1.5 mt-2"
                          >
                            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                            {submitErrors.cart}
                          </p>
                        )}
                        {submitErrors.submit && (
                          <div
                            role="alert"
                            aria-live="assertive"
                            className="flex items-start gap-2 p-3 mt-2 bg-destructive/10 border border-destructive/20 rounded-lg"
                          >
                            <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                            <p className="text-sm text-destructive">
                              {submitErrors.submit}
                            </p>
                          </div>
                        )}
                      </div>
                    </ResponsiveDialogFooter>
                  )}
                </>
              )}
            </ResponsiveDialogContent>
          </ResponsiveDialog>
        }
      ></ProductSelectionHeader>
      {/* Content area — instructions + form share the same container as the header */}
      <div className="px-4 sm:px-6 pt-4">
        {/* Instructions banner */}
        {(instructionText ?? noCatalogueConfig.additionalDetails) && (
          <div className="mb-4">
            <div className="bg-accent border border-border rounded-lg p-3">
              <div className="flex items-start gap-3">
                <ClipboardList className="h-5 w-5 text-accent-foreground mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-medium text-accent-foreground text-sm">
                    Instructions
                  </h3>
                  <div
                    className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap prose prose-sm max-w-none dark:prose-invert"
                    // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
                    dangerouslySetInnerHTML={{
                      __html: safeHtml,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Product Form */}
        <ProductRequestForm
          noCatalogueConfig={noCatalogueConfig}
          onAddToCart={handleAddToCart}
          remainingQuantity={
            quantityMax != null
              ? Math.max(quantityMax - cartItemCount, 0)
              : null
          }
        />
      </div>
    </div>
  );
}
